import { useCallback, useMemo } from "react";
import { useNavigation } from "@react-navigation/native";

import { Button, Col, Col1, Content, TextLabel } from "../lib";
import {
  actions,
  getTierName,
  openUrl,
  useCities,
  useConfig,
  UserValidator,
  useUser,
} from "../services";
import { LoadingScreen } from "./loading";
import { createStyles, sizes } from "../theme";
import {
  Banner,
  CalloutRefresh,
  MenuListItem,
  ProfileHeader,
  RatingScore,
} from "../components";
import { ScrollView } from "react-native";
import { Env } from "../env";

export function ProfileScreen() {
  // Data
  const {
    data: cities,
    isLoading: isCitiesLoading,
    refetch: refreshCities,
  } = useCities();
  const {
    data: config,
    isLoading: isConfigLoading,
    refetch: refreshConfig,
  } = useConfig();
  const {
    data: user,
    isLoading: isUserLoading,
    refetch: refreshUser,
  } = useUser();

  // Vars
  const navigation = useNavigation();
  const city = useMemo(() => {
    return cities?.find((_city) => _city.id === user?.city?.id) ?? null;
  }, [cities, user]);

  // Methods
  const refresh = useCallback(() => {
    refreshUser();
    refreshConfig();
    refreshCities();
  }, [refreshUser, refreshConfig, refreshCities]);

  const goToIdentity = useCallback(() => {
    navigation.navigate("Identity");
  }, [navigation]);

  const goToBody = useCallback(() => {
    navigation.navigate("Body");
  }, [navigation]);

  const goToLanguages = useCallback(() => {
    navigation.navigate("Languages");
  }, [navigation]);

  const goToServices = useCallback(() => {
    navigation.navigate("Services");
  }, [navigation]);

  const goToRates = useCallback(() => {
    navigation.navigate("Rates");
  }, [navigation]);

  const goToPhotos = useCallback(() => {
    navigation.navigate("Photos");
  }, [navigation]);

  const goToSocials = useCallback(() => {
    navigation.navigate("Socials");
  }, [navigation]);

  const goToContact = useCallback(() => {
    navigation.navigate("Contact");
  }, [navigation]);

  const goToIncall = useCallback(() => {
    navigation.navigate("Incall");
  }, [navigation]);

  const goToCityAreas = useCallback(() => {
    navigation.navigate("CityAreas");
  }, [navigation]);

  const goToPremium = useCallback(() => {
    navigation.navigate("Premium");
  }, [navigation]);

  const goToReviews = useCallback(() => {
    navigation.navigate("Reviews");
  }, [navigation]);

  const handlePressTelegram = useCallback(() => {
    openUrl("https://t.me/smoociag_bot?start=" + user?.hash + "-" + user?.otp);
  }, [user]);

  // Render
  if (isUserLoading || isConfigLoading || isCitiesLoading) {
    return <LoadingScreen />;
  }

  if (!user || !config) {
    return <CalloutRefresh onRefresh={refresh} />;
  }

  const tierName = getTierName(user.premium_tier);

  return (
    <Content pt="0" px="0">
      {!!user.offer && (
        <Banner
          icon="star-circle"
          onPress={goToPremium}
          message={user.offer.promotion.name}
        />
      )}
      <ProfileHeader
        user={user}
        onPressAvatar={goToPhotos}
        onPressPremium={goToPremium}
      />
      <ScrollView
        style={$.actionsContainer}
        contentContainerStyle={$.actionsContent}
        horizontal
        showsHorizontalScrollIndicator={false}
      >
        <Button compact onPress={handlePressTelegram}>
          Telegram notifications
        </Button>
        <Button compact onPress={actions.stopSession}>
          Log out
        </Button>
      </ScrollView>
      <Col1>
        <Col style={$.menu}>
          <MenuListItem
            id="premium"
            label="Membership"
            addon={
              user.premium ? (
                <TextLabel>{tierName?.toUpperCase() ?? "PREMIUM"}</TextLabel>
              ) : null
            }
            onPress={goToPremium}
          />
          <MenuListItem
            id="reviews"
            label="Reviews"
            addon={<RatingScore value={user.score} />}
            onPress={goToReviews}
            last
          />
        </Col>
        <Col style={$.menu} mt="8">
          <MenuListItem
            id="socials"
            label="Contact details"
            onPress={goToSocials}
            invalid={!UserValidator.socials(user)}
          />
          <MenuListItem
            id="photos"
            label="Photos"
            onPress={goToPhotos}
            invalid={!UserValidator.images(user)}
          />
          <MenuListItem
            id="identity"
            label="Profile"
            onPress={goToIdentity}
            invalid={
              !UserValidator.identity(user) || !UserValidator.verified(user)
            }
          />
          <MenuListItem
            id="body"
            label="Measurements"
            onPress={goToBody}
            invalid={!UserValidator.body(user)}
          />
          <MenuListItem
            id="languages"
            label="Languages"
            onPress={goToLanguages}
            invalid={!UserValidator.languages(user)}
          />
          <MenuListItem
            id="services"
            label="Services"
            onPress={goToServices}
            invalid={!UserValidator.services(user)}
          />
          <MenuListItem
            id="rates"
            label="Rates"
            onPress={goToRates}
            invalid={!UserValidator.rates(user)}
            last={!config.incalls && !city?.areas.length}
          />
          {!!config.incalls && (
            <MenuListItem
              id="incall"
              label="Incall"
              onPress={goToIncall}
              invalid={!UserValidator.incall(user, config)}
              last={!city?.areas.length}
            />
          )}
          {!!city?.areas.length && (
            <MenuListItem
              id="city-areas"
              label="City Areas"
              onPress={goToCityAreas}
              invalid={!UserValidator.cityAreas(user, city)}
              last
            />
          )}
        </Col>

        <Col style={$.menu} my="8">
          {/* <MenuListItem
            id="tutorial"
            label="Tutorial"
            url="https://smooci.com/help/companion"
          /> */}
          <MenuListItem id="contact" label="Contact us" onPress={goToContact} />
          <MenuListItem
            id="privacy"
            label="Privacy Policy"
            url="https://smooci.com/privacy"
          />
          <MenuListItem
            id="terms"
            label="Terms & Conditions"
            url="https://smooci.com/terms"
            last
          />
        </Col>
        <TextLabel center>v{Env.version}</TextLabel>
      </Col1>
    </Content>
  );
}

const $ = createStyles({
  actionsContainer: {
    flexGrow: 0,
  },
  actionsContent: {
    gap: sizes["2"],
    px: "4",
    mb: "4",
  },
  menu: {
    backgroundColor: "surface",
  },
});
